import React, { useEffect, useRef } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AppLanguage } from './model/enums/AppLanguage';
import { useCookieConsent } from './helpers/useCookieConsent';
import { SideBanner } from './components/SideBanner';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { isHomePath } from './helpers/useUseCase';

export const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const cookieConsent = useCookieConsent();
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const lang = i18n.language.startsWith('cs') ? AppLanguage.Czech : AppLanguage.English;
    cookieConsent.run(lang);
  }, [i18n.language, cookieConsent]);

  // Links between pages carry the target section in the hash ('/#features'),
  // so the anchor has to be resolved once the new page has been rendered.
  useEffect(() => {
    if (!hash) {
      return;
    }
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [pathname, hash]);

  // Opening another page from halfway down the current one would otherwise keep
  // the scroll position and look like nothing happened. Picking a use case keeps
  // the visitor on the home page, so it keeps the position.
  const previousPathname = useRef(pathname);
  useEffect(() => {
    const switchedUseCase = isHomePath(previousPathname.current) && isHomePath(pathname);
    if (previousPathname.current !== pathname && !hash && !switchedUseCase) {
      window.scrollTo(0, 0);
    }
    previousPathname.current = pathname;
  }, [pathname, hash]);

  return (
    <>
      <Header />
      {children}
      <SideBanner />
      <Footer />
    </>
  );
};
